/**
 * usePublicProductFilters Hook - Story 7.2: Create Product Browse/Listing Page
 * Manages filter state for public product browsing with URL search params persistence
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ProductBrowseFilters } from '../types/publicMarketplace.types';
import type { ProductCategory } from '../types/products.types';

/**
 * Default filter state for public product browsing
 */
const DEFAULT_FILTERS: ProductBrowseFilters = {
  search: '',
  category: null,
  state: null,
  district: null,
  sortBy: 'newest',
  page: 0,
  pageSize: 20,
};

/**
 * Debounce timeout reference
 */
let searchDebounceTimeout: NodeJS.Timeout | null = null;

/**
 * Return type for usePublicProductFilters hook
 */
export interface UsePublicProductFiltersReturn {
  /** Current filter state */
  filters: ProductBrowseFilters;
  /** Get current filter state (utility method) */
  getFilters: () => ProductBrowseFilters;
  /** Update search query with debouncing (300ms) */
  setSearch: (query: string) => void;
  /** Update category filter */
  setCategory: (category: ProductCategory | null) => void;
  /** Update state filter */
  setState: (state: string | null) => void;
  /** Update district filter */
  setDistrict: (district: string | null) => void;
  /** Update sort order */
  setSortBy: (sortBy: 'newest' | 'price_low' | 'price_high') => void;
  /** Navigate to specific page */
  goToPage: (page: number) => void;
  /** Reset all filters and go to page 0 */
  resetFilters: () => void;
  /** Check if any non-default filters are active */
  hasActiveFilters: boolean;
}

/**
 * usePublicProductFilters Hook
 *
 * Manages filter state for public product browsing with URL search params persistence.
 * Implements debouncing for search input and automatic pagination reset on filter changes.
 *
 * @returns Filter state and management functions
 *
 * @example
 * ```tsx
 * const {
 *   filters,
 *   setSearch,
 *   setCategory,
 *   setState,
 *   setSortBy,
 *   goToPage,
 *   resetFilters,
 *   hasActiveFilters
 * } = usePublicProductFilters();
 *
 * // Update filters
 * setSearch('biogas equipment');
 * setCategory('EQUIPMENT');
 * setState('Karnataka');
 * setSortBy('price_low');
 * goToPage(2);
 *
 * // Check if filters are active
 * if (hasActiveFilters) {
 *   // Show clear filters button
 * }
 *
 * // Reset all filters
 * resetFilters();
 * ```
 */
export function usePublicProductFilters(): UsePublicProductFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFiltersState] = useState<ProductBrowseFilters>(DEFAULT_FILTERS);
  const [pendingSearch, setPendingSearch] = useState<string>('');

  /**
   * Initialize filters from URL search params on mount
   */
  useEffect(() => {
    const initialFilters: ProductBrowseFilters = {
      search: searchParams.get('search') || '',
      category: (searchParams.get('category') as ProductCategory) || null,
      state: searchParams.get('state') || null,
      district: searchParams.get('district') || null,
      sortBy: (searchParams.get('sortBy') as 'newest' | 'price_low' | 'price_high') || 'newest',
      page: parseInt(searchParams.get('page') || '0', 10),
      pageSize: parseInt(searchParams.get('pageSize') || '20', 10),
    };
    setFiltersState(initialFilters);
    setPendingSearch(initialFilters.search || '');
  }, []); // Only run on mount

  /**
   * Sync filters to URL search params whenever they change
   */
  useEffect(() => {
    const newParams = new URLSearchParams();

    if (filters.search) {
      newParams.set('search', filters.search);
    }
    if (filters.category) {
      newParams.set('category', filters.category);
    }
    if (filters.state) {
      newParams.set('state', filters.state);
    }
    if (filters.district) {
      newParams.set('district', filters.district);
    }
    if (filters.sortBy !== 'newest') {
      newParams.set('sortBy', filters.sortBy);
    }
    if (filters.page > 0) {
      newParams.set('page', filters.page.toString());
    }
    if (filters.pageSize !== 20) {
      newParams.set('pageSize', filters.pageSize.toString());
    }

    setSearchParams(newParams, { replace: true });
  }, [filters, setSearchParams]);

  /**
   * Get current filter state
   */
  const getFilters = useCallback((): ProductBrowseFilters => {
    return { ...filters };
  }, [filters]);

  /**
   * Update search query with debouncing (300ms)
   */
  const setSearch = useCallback((query: string) => {
    // Update pending search immediately for UI responsiveness
    setPendingSearch(query);

    // Clear existing timeout
    if (searchDebounceTimeout) {
      clearTimeout(searchDebounceTimeout);
    }

    // Debounce the actual filter update
    searchDebounceTimeout = setTimeout(() => {
      setFiltersState((current) => ({
        ...current,
        search: query,
        page: 0, // Reset to page 0 when search changes
      }));
    }, 300);
  }, []);

  /**
   * Update category filter
   */
  const setCategory = useCallback((category: ProductCategory | null) => {
    setFiltersState((current) => ({
      ...current,
      category,
      page: 0, // Reset to page 0 when category changes
    }));
  }, []);

  /**
   * Update state filter
   */
  const setState = useCallback((state: string | null) => {
    setFiltersState((current) => ({
      ...current,
      state,
      district: null, // Clear district when state changes
      page: 0, // Reset to page 0 when state changes
    }));
  }, []);

  /**
   * Update district filter
   */
  const setDistrict = useCallback((district: string | null) => {
    setFiltersState((current) => ({
      ...current,
      district,
      page: 0, // Reset to page 0 when district changes
    }));
  }, []);

  /**
   * Update sort order
   */
  const setSortBy = useCallback((sortBy: 'newest' | 'price_low' | 'price_high') => {
    setFiltersState((current) => ({
      ...current,
      sortBy,
      page: 0, // Reset to page 0 when sort changes
    }));
  }, []);

  /**
   * Navigate to specific page
   */
  const goToPage = useCallback((page: number) => {
    setFiltersState((current) => ({
      ...current,
      page: Math.max(0, page), // Ensure page is never negative
    }));
  }, []);

  /**
   * Reset all filters and go to page 0
   */
  const resetFilters = useCallback(() => {
    setPendingSearch('');
    setFiltersState(DEFAULT_FILTERS);

    // Clear debounce timeout if active
    if (searchDebounceTimeout) {
      clearTimeout(searchDebounceTimeout);
      searchDebounceTimeout = null;
    }
  }, []);

  /**
   * Check if any non-default filters are active
   */
  const hasActiveFilters = useMemo(() => {
    return (
      filters.search !== DEFAULT_FILTERS.search ||
      filters.category !== DEFAULT_FILTERS.category ||
      filters.state !== DEFAULT_FILTERS.state ||
      filters.district !== DEFAULT_FILTERS.district ||
      filters.sortBy !== DEFAULT_FILTERS.sortBy
    );
  }, [filters]);

  /**
   * Cleanup debounce timeout on unmount
   */
  useEffect(() => {
    return () => {
      if (searchDebounceTimeout) {
        clearTimeout(searchDebounceTimeout);
      }
    };
  }, []);

  return {
    filters: {
      ...filters,
      search: pendingSearch, // Use pending search for immediate UI feedback
    },
    getFilters,
    setSearch,
    setCategory,
    setState,
    setDistrict,
    setSortBy,
    goToPage,
    resetFilters,
    hasActiveFilters,
  };
}

/**
 * Build query string from filter state
 *
 * Utility function to convert ProductBrowseFilters to URL query string.
 * Omits default values to keep URLs clean.
 *
 * @param filters - Filter state object
 * @returns Query string (without leading '?')
 *
 * @example
 * ```ts
 * const filters: ProductBrowseFilters = {
 *   search: 'biogas',
 *   category: 'EQUIPMENT',
 *   state: 'Karnataka',
 *   district: null,
 *   sortBy: 'price_low',
 *   page: 2,
 *   pageSize: 20,
 * };
 *
 * const queryString = buildFilterQueryString(filters);
 * // Returns: "search=biogas&category=EQUIPMENT&state=Karnataka&sortBy=price_low&page=2"
 * ```
 */
export function buildFilterQueryString(filters: ProductBrowseFilters): string {
  const params = new URLSearchParams();

  // Add non-empty/non-default values to query string
  if (filters.search) {
    params.set('search', filters.search);
  }
  if (filters.category) {
    params.set('category', filters.category);
  }
  if (filters.state) {
    params.set('state', filters.state);
  }
  if (filters.district) {
    params.set('district', filters.district);
  }
  if (filters.sortBy && filters.sortBy !== 'newest') {
    params.set('sortBy', filters.sortBy);
  }
  if (filters.page > 0) {
    params.set('page', filters.page.toString());
  }
  if (filters.pageSize !== 20) {
    params.set('pageSize', filters.pageSize.toString());
  }

  return params.toString();
}

/**
 * Parse query string to filter state
 *
 * Utility function to convert URL query string to ProductBrowseFilters.
 * Provides defaults for missing values.
 *
 * @param queryString - URL query string (with or without leading '?')
 * @returns Filter state object
 *
 * @example
 * ```ts
 * const queryString = "search=biogas&category=EQUIPMENT&page=2";
 * const filters = parseFilterQueryString(queryString);
 * // Returns: {
 * //   search: 'biogas',
 * //   category: 'EQUIPMENT',
 * //   state: null,
 * //   district: null,
 * //   sortBy: 'newest',
 * //   page: 2,
 * //   pageSize: 20
 * // }
 * ```
 */
export function parseFilterQueryString(queryString: string): ProductBrowseFilters {
  const params = new URLSearchParams(queryString);

  return {
    search: params.get('search') || '',
    category: (params.get('category') as ProductCategory) || null,
    state: params.get('state') || null,
    district: params.get('district') || null,
    sortBy: (params.get('sortBy') as 'newest' | 'price_low' | 'price_high') || 'newest',
    page: parseInt(params.get('page') || '0', 10),
    pageSize: parseInt(params.get('pageSize') || '20', 10),
  };
}
